import "../styles/InventoryTable.css";
import "../styles/InventoryPicModal.css";
import handleImageErrorWithPasomaruFallback from '../utils/imageUtils';
import { apiRequest } from "../utils/api";
import { generateJan } from "../utils/jan"; 
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle} from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useAlert } from '../components/AlertModal';
import Modal from 'react-modal';
import { useScrollPersistence } from "../hooks/useScrollPersistence";
import { useReturnParams } from "../hooks/useReturnParams";

Modal.setAppElement('#root');  // アクセシビリティ設定


const InventoryTable = forwardRef(({ items, storeId, fetchItems, }, ref) => {
  let stockUpdateTimer = null;
  const [editValues, setEditValues] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);  // 画像リストを管理
  const [modalIsOpen, setModalIsOpen] = useState(false);  // モーダル表示状態
  const [selectedImage, setSelectedImage] = useState(null);  // 選択した画像
  const navigate = useNavigate();// ページ移動
  const location = useLocation();
  const { showAlert } = useAlert();
  const inventoryContainerScrollRef = useRef(null);
  const { saveReturnParams } = useReturnParams();
  const { saveScroll } = useScrollPersistence(inventoryContainerScrollRef, "inventory_container_scroll", items.length); // スクロール保存
  
  // カテゴリー情報を取得
  useEffect(() => {
    const controller = new AbortController();

    const fetchCategories = async () => {
      try {
        const response = await apiRequest(
          "inventory/categorys/",
          "GET",
          null,
          {},
          true,
          controller.signal
        );
        setCategories(response);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("カテゴリー取得失敗:", error);
      }
    };

    fetchCategories();

    return () => {
      controller.abort();
    };
  }, []); 
  
  const filteredItems = items.filter(
    (item) =>
      item.store_inventory_set &&
      item.store_inventory_set.some((s) => s.store === storeId)
  );

  const getStoreInventory = (item) =>
    item.store_inventory_set?.find((inv) => inv.store === storeId);
  
  const getCompetitorStore = (item) =>
    item.competitorstore_set?.find((inv) => inv.store_name === "駿河屋");

  const handleChange = (id, field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

const handleBlur = (item) => {
  const edited = editValues[item.id];
  if (!edited) return;

  const inventoryUpdates = {};
  const storeInventoryUpdates = {};
  const storeInv = getStoreInventory(item) || {};
  const surugaya = getCompetitorStore(item);

  for (const key in edited) {
    if ([
      "jan", "item_name", "manufacturer", "sku",
      "title", "genre1", "genre2", "category_id",  // category_nameはcategory_idに変更
      "picture_switch", "csv_switch", "release_date", "note"
    ].includes(key)) {
      if (edited[key] !== item[key]) {
        inventoryUpdates[key] = edited[key];
      }
    } else if (key === "competitor_productid") {
      if (edited[key] !== surugaya?.competitor_productid) {
        handleSurugayaIdUpdate(surugaya?.id, item.id, edited[key]);
      }
    } else {
      if (edited[key] !== storeInv[key]) {
        storeInventoryUpdates[key] = edited[key];
      }
    }
  }

  if (Object.keys(inventoryUpdates).length > 0) {
    handleInventoryUpdate(item.id, inventoryUpdates);
  }
  if (Object.keys(storeInventoryUpdates).length > 0) {
    submitStoreInventoryUpdate(item.id, storeInventoryUpdates, storeInv?.id);
  }
};

  // 商品マスター更新
  const handleInventoryUpdate = async (itemId, updates) => {
    try {
      await apiRequest(`inventory/inventory/${itemId}/`, 'PATCH', updates);
      fetchItems();
      setEditValues((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    } catch (error) {
      console.error("Inventory更新失敗:", error);
    }
  };
  // 在庫関連データ更新
  const submitStoreInventoryUpdate = async (itemId, updates, storeInventoryId) => {
    const isNew = !storeInventoryId;
    const endpoint = isNew ? "inventory/storeinventory/" : `inventory/storeinventory/${storeInventoryId}/`;
    const method = isNew ? "POST" : "PATCH";
    const storeInv = getStoreInventory(items.find(i => i.id === itemId))

    const payload = {
      ...(isNew ? { inventory: itemId, store: storeId } : {}),
      ...updates,
      version_buy: storeInv?.version_buy ?? 1,
    };

    try {
      await apiRequest(endpoint, method, payload);
      fetchItems();
      setEditValues((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    } catch (error) {
      if  (error.status === 409) {
        // 最新取得して上書き
        showAlert("他で変更された可能性があります。最新状態で上書きしました。");
        const latestItem = await apiRequest(`inventory/inventory/${itemId}/`);
        const latestStoreInv = latestItem.store_inventory_set.find((inv) => inv.store === storeId);

        await apiRequest(endpoint, method, {
          ...payload,
          version_buy: latestStoreInv.version_buy,  // 最新バージョンで上書き
        });
        fetchItems();
        setEditValues((prev) => {
          const newState = { ...prev };
          delete newState[itemId];
          return newState;
        });
      } else {
        console.error("StoreInventory更新失敗:", error);
      }
    }
  };
  // 競合店ID更新
  const handleSurugayaIdUpdate = async (competitorId, itemId, value) => {
    try {
      if (competitorId) {
        await apiRequest(`inventory/competitorstore/${competitorId}/`, 'PATCH', {
          competitor_productid: value
        });
      } else {
        await apiRequest(`inventory/competitorstore/`, 'POST', {
          store_name: "駿河屋",
          inventory: itemId,
          competitor_productid: value,
        });
      }
      fetchItems();
    } catch (error) {
      console.error("駿河屋ID更新失敗:", error);
    }
  };

  const handleCheckboxChange_I = (item, field, checked) => {
    const updates = { [field]: checked };
    handleInventoryUpdate(item.id, updates);
  };

  const handleCheckboxChange = async (item, field, checked) => {
    if (field === "buy_top_switch" && checked) {
      const categoryId = item.category?.id;
  
      let categoryGroup = null;
      if ([1, 2].includes(categoryId)) categoryGroup = '1or2';
      if ([3, 4].includes(categoryId)) categoryGroup = '3or4';
      if (!categoryGroup) {
        console.log("⚠ 不明なカテゴリ:", categoryId);
        return;
      }
  
      try {
        const res = await apiRequest(`inventory/top_switch_count/?store_id=${storeId}&category_group=${categoryGroup}`);
        console.log("🎯 サーバーからの現在TOP数:", res.count);
        if (res.count >= 9) {
          showAlert(`TOPスイッチの数が多すぎます。減らしてから指定して下さい。`);
          return;
        }
      } catch (err) {
        console.error("TOPスイッチ件数確認失敗:", err);
        showAlert("TOP件数の確認に失敗しました");
        return;
      }
    }
  
    const storeInv = getStoreInventory(item);
    const storeInventoryId = storeInv?.id;
    const updates = { [field]: checked };
    submitStoreInventoryUpdate(item.id, updates, storeInventoryId);
  };

  const handleAddToLabel = async (id, price) => {
    try {
      const response = await apiRequest(`inventory/add_to_label/`, "POST", {
        id,
        price,
      });

      if (response.message) {
        showAlert(response.message);
      }
    } catch (error) {
      console.error("Error:", error);
      showAlert("エラーが発生しました");
    }
  };
  
  const handleDetail = (item) => {
    saveScroll();
    saveReturnParams(location.pathname + location.search);
    navigate(`/inventory_detail/${item.id}/${storeId}`);
  };

  const handleUpdateStock = async (item, storeId, newStock) => {
    const storeInventory = getStoreInventory(item);
    const itemId = item.id;
    const currentStock = storeInventory?.stock ?? 0;
    const difStock = newStock - currentStock;
    const version = storeInventory?.version ?? 1;

    if (difStock === 0) return;

    try {
      await apiRequest("inventory/inventory_adjustments/", "POST", {
        inventory: itemId,
        store_id: storeId,
        quantity_change: difStock,
        reason: "在庫調整",
        version: version,  // version送信
      });
      fetchItems();
    } catch (error) {
      if (error.message.includes("409")) {
        showAlert("在庫数が他で更新されました。最新の在庫を取得します。");
        fetchItems();
      } else {
        console.error("在庫上書き失敗:", error);
      }
    }
  };

  const handleDelayedStockUpdate = (item, storeId, value) => {
    clearTimeout(stockUpdateTimer);
    stockUpdateTimer = setTimeout(() => {
      handleUpdateStock(item, storeId, Number(value));
    }, 1500); // 1500ms待機して更新
  };
    // フォーカス時に全選択する関数
  const handleFocus = (e) => {
    e.target.select();  // フォーカス時に全選択
  };
  
  const handgenerateJan = async (id,jan) => {
      if (jan===""  ) {
        handleChange(id, "jan", generateJan(id))
        const updates = { "jan": generateJan(id) };
        handleInventoryUpdate(id, updates);
      }else{
        showAlert("JANが既にあります。削除してから押してください。");
      }
  };
  
  const handleAddToImageList = (item) => {
    setSelectedImage(item);  // 選択した画像を保存
    setModalIsOpen(true);  // モーダルを開く
  };

  const handleWidthChange = (width) => {
    const imageUrl = `/media/product_images/${selectedImage.sku}.jpg`;  // 商品画像のURL

    // 画像をリストに追加
    setSelectedImages((prevImages) => [
      ...prevImages,
      { url: imageUrl, width: width }
    ]);
    setModalIsOpen(false);  // モーダルを閉じる
  };

  const handlePrintPreview = () => {
    // 印刷プレビュー用のページに移動
    navigate("/picprint_preview", { state: { images: selectedImages } });
  };
  const handleBuyPrintPreview = (id) => {
    console.log(id)
    // 印刷プレビュー用のページに移動
    navigate("/buypicprint_preview", { state: {  id } });
  };
  // スクロール 

  const scrollToTop = () => {
    const el = inventoryContainerScrollRef.current;
    if (el) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useImperativeHandle(ref, () => ({
    scrollToTop,
  }));

  const scrollToBottom = () => {
    const content = document.querySelector('.inventory_container_scroll');
    if (content) {
      content.scrollTo({ top: content.scrollHeight, behavior: 'smooth' });
    }
  };

  return (
    <div ref={inventoryContainerScrollRef} className="inventory_container_scroll">
    <div className="inventory_container">
      <div className="scroll-buttons">
        {/* TOPボタン */}
        <button className="scroll-top-btn" onClick={scrollToTop}>
          <div className="triangle-up" />
          <div className="scroll-buttons-label">TOP</div>
        </button>

        {/* BOTTOMボタン */}
        <button className="scroll-bottom-btn" onClick={scrollToBottom}>
          <div className="triangle-down" />
          <div className="scroll-buttons-label">BOTTOM</div>
        </button>
      </div>
        {/* データ表示 */}
        {filteredItems.length > 0 ? (
        filteredItems.map((item) => (
      <div key={item.id} data-id={item.id} className="table-container">
        <div className="pcjan" onClick={() => handgenerateJan(item.id,editValues[item.id]?.jan ?? item.jan?? "")}><span>JAN</span>：</div>
        <div className="pcsku">発売日：</div>
        <div className="pcbuyprice">買取価格：¥</div>
        <div className="pcsbuyprice">特別価格：¥</div>      
        <div className="pcstock">在庫：</div>
        <div className="pcupdate">更新日：</div>
        <div className="pcsellprice">販売価格：¥</div>
        <div className="pcsetprice">SET売価：¥</div>
        <div className="pcsurugaid">駿河ID：</div>
        <div className="pctitle">作品名：</div>
        <div className="pcgenre1">ジャンル1：</div>
        <div className="pcgenre2">ジャンル2：</div>
        <div className="pccategory">CAT：</div>
        <div className="recopic">
          <img
            src={`/media/product_images/${item.sku}.jpg?`}
            alt="商品画像"
            onError={(e) => handleImageErrorWithPasomaruFallback(e, item.sku)}
          />
        </div>
        <div className="picprint"><button onClick={() => handleAddToImageList(item)}>追加</button></div>
        <div className="picprintPreview"><button onClick={handlePrintPreview}>印刷</button></div>
        <div className="buyprintPreview"><button onClick={() => handleBuyPrintPreview(item.id)}>買取印刷</button></div>
        <div className="recolabel"><button className="inventory_table_add_to_label" onClick={() => handleAddToLabel(item.id, (item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0))}>🖨</button></div>
        <div className="recoid"><button className="inventory_table_item_id" onClick={() => handleDetail(item)}>📝<br/>{item.id}</button></div>
        <div className="recojan">
              <input
                type="text"
                value={editValues[item.id]?.jan ?? item.jan?? ""}
                onChange={(e) => handleChange(item.id, "jan", e.target.value)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        <div className="reconame">
              <input
                type="text"
                value={editValues[item.id]?.item_name ?? item.item_name?? ""}
                onChange={(e) => handleChange(item.id, "item_name", e.target.value)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        
        <div className="recomeka">
              <input
                type="text"
                value={editValues[item.id]?.manufacturer ?? item.manufacturer?? ""}
                onChange={(e) => handleChange(item.id, "manufacturer", e.target.value)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        <div className="recosku">
              <input
                type="text"
                value={editValues[item.id]?.release_date ?? item.release_date ?? ""}
                onChange={(e) => handleChange(item.id, "release_date", e.target.value)}
                onBlur={(e) => {
                  const input = e.target.value;
                  const normalized = input.replace(/\//g, "-");
                  const matched = normalized.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);

                  if (matched) {
                    const yyyy = matched[1];
                    const mm = String(matched[2]).padStart(2, "0");
                    const dd = String(matched[3]).padStart(2, "0");
                    handleChange(item.id, "release_date", `${yyyy}-${mm}-${dd}`);
                  }

                  handleBlur(item);
                }}
              /></div>
        <div className="recobuyprice">
              <input
                type="text"
                value={editValues[item.id]?.buy_price ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_price ?? 0)}
                onChange={(e) => handleChange(item.id, "buy_price", e.target.value, storeId)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              />
        </div>
        <div className="recotopswitch">
              <input
                type="checkbox"
                id={`topswitch${item.id}`} 
                checked={editValues[item.id]?.buy_top_switch ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_top_switch ?? false)}
                onChange={(e) => {
                  handleCheckboxChange(item, "buy_top_switch", e.target.checked, storeId);
                }}             
              />
              <label htmlFor={`topswitch${item.id}`} className="checkbox_button checkbox1">TOP</label>
              </div>
        <div className="recosbuyprice">
              <input
                type="text"
                value={editValues[item.id]?.buy_specialprice ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_specialprice?? 0)}
                onChange={(e) => handleChange(item.id, "buy_specialprice", e.target.value, storeId)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              />
        </div>
        <div className="recosbuyswitch">
          <input
            type="checkbox"
            id={`spbuyswitch${item.id}`} 
            checked={editValues[item.id]?.buy_specialprice_switch ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_specialprice_switch ?? false)}
            onChange={(e) => {
              handleCheckboxChange(item, "buy_specialprice_switch", e.target.checked, storeId); // ← 直接即時更新
            }}
          />
          <label htmlFor={`spbuyswitch${item.id}`} className="checkbox_button checkbox2">特別</label>
        </div>
        <div className="recoshopswitch"> 
              <input
                type="checkbox"
                id={`spmiseswitch${item.id}`} 
                checked={editValues[item.id]?.buy_specialprice_shoponly ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.buy_specialprice_shoponly ?? false)}
                onChange={(e) => {
                  handleCheckboxChange(item, "buy_specialprice_shoponly", e.target.checked, storeId);
                }}
              />
              <label htmlFor={`spmiseswitch${item.id}`} className="checkbox_button checkbox3">店限</label>
              </div>
        <div className="recostock">{item.stock} 
              <input
                type="number"
                value={
                  editValues[item.id]?.stock ??
                  item.store_inventory_set?.find(inv => inv.store === storeId)?.stock ?? 0
                }
                readonly
              /></div>
        <div className="recoupdate">
              <input
                type="text"
                value={editValues[item.id]?.update_date ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.update_date ?? "")}
                onChange={(e) => handleChange(item.id, "update_date", e.target.value, storeId)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        <div className="recosellprice">
              <input
                type="text"
                value={editValues[item.id]?.sell_price ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_price ?? 0)}
                onChange={(e) => handleChange(item.id, "sell_price", e.target.value, storeId)}              
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        <div className="recosetprice">{item.sell_set_price} 
              <input
                type="text"
                value={editValues[item.id]?.sell_set_price ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.sell_set_price ?? 0)}
                onChange={(e) => handleChange(item.id, "sell_set_price", e.target.value, storeId)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        <div className="recosurugaya">
              <input
                type="text"
                value={editValues[item.id]?.competitor_productid ?? (item.competitorstore_set?.find((inv) => inv.store_name === "駿河屋")?.competitor_productid ?? "")} 
                onChange={(e) => handleChange(item.id, "competitor_productid", e.target.value)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              />
              </div>
        <div className="recotitle">
              <input
                type="text"
                value={editValues[item.id]?.title ?? item.title ?? ""}
                onChange={(e) => handleChange(item.id, "title", e.target.value)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        <div className="recogenre1">
              <input
                type="text"
                value={editValues[item.id]?.genre1 ?? item.genre1 ?? ""}
                onChange={(e) => handleChange(item.id, "genre1", e.target.value)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        <div className="recogenre2">
              <input
                type="text"
                value={editValues[item.id]?.genre2 ?? item.genre2 ?? ""}
                onChange={(e) => handleChange(item.id, "genre2", e.target.value)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>
        <div className="reconote">
              <textarea
                type="text"
                value={editValues[item.id]?.note ?? item.note ?? ""}
                onChange={(e) => handleChange(item.id, "note", e.target.value)}
                onBlur={() => handleBlur(item)}
                onFocus={handleFocus}
              /></div>

        <div className="recocategory">
            <select
              className="recocategory_select"
              value={editValues[item.id]?.category_id ?? item.category?.id ?? ""}
              onChange={(e) => handleChange(item.id, "category_id", e.target.value)}
              onBlur={() => handleBlur(item)}
            >
              <option value="">選択してください</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.category_name}
                </option>
              ))}
            </select></div>
        <div className="recosstock">
              <input
                type="checkbox"
                id={`sstockswitch${item.id}`} 
                checked={editValues[item.id]?.scale_stock_switch ?? (item.store_inventory_set?.find(inv => inv.store === storeId)?.scale_stock_switch ?? false)} 
                onChange={(e) => {
                  handleCheckboxChange(item, "scale_stock_switch", e.target.checked);
                }}
              />
              <label htmlFor={`sstockswitch${item.id}`} className="checkbox_button checkbox4">封印中</label>
              </div>
        <div className="recopics">
              <input
                type="checkbox"
                id={`picswitch${item.id}`} 
                checked={editValues[item.id]?.picture_switch ?? item.picture_switch ?? false}
                onChange={(e) => {
                  handleCheckboxChange_I(item, "picture_switch", e.target.checked);
                }}
              />
              <label htmlFor={`picswitch${item.id}`} className="checkbox_button checkbox5">PIC404</label>
              </div>
        <div className="recocsv">
              <input
                type="checkbox"
                id={`csvswitch${item.id}`} 
                checked={editValues[item.id]?.csv_switch ?? item.csv_switch ?? false}
                onChange={(e) => {
                  handleCheckboxChange_I(item, "csv_switch", e.target.checked);
                }}
              />
              <label htmlFor={`csvswitch${item.id}`} className="checkbox_button checkbox6">NO-CSV</label>
              </div>
        <div className="amazon">
          <a href={`https://www.amazon.co.jp/s?k=${item.jan}`} target="_blank" rel="noopener noreferrer">
          <button className="amozon_button">Amazon</button>
          </a>
              </div>
        <div className="amiami">
          <a href={`https://slist.amiami.jp/top/search/list9?s_keywords=${item.jan}&submit=%E6%A4%9C%E7%B4%A2&pagemax=20&s_buy_flg=1`} target="_blank" rel="noopener noreferrer">
          <button className="amiami_button">あみあみ</button>
          </a>
              </div>
        <div className="surugaya">
          <a
            href={`https://www.suruga-ya.jp/kaitori/search_buy?category=&search_word=${
              editValues[item.id]?.competitor_productid
                ?? item.competitorstore_set?.find((inv) => inv.store_name === "駿河屋")?.competitor_productid
                ?? (
                  !item.jan || item.jan === ""
                    ? item.item_name
                    : item.jan.length === 18
                      ? item.jan.slice(-9)
                      : item.jan.startsWith("20137")
                        ? item.item_name
                        : item.jan
                )
            }&searchbox=1`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="surugaya_button">駿河屋</button>
          </a>
        </div>
        <div className="mercari">
          <a href={`https://jp.mercari.com/search?keyword=${item.item_name}&item_condition_id=1&sort=score&order=desc&status=on_sale`} target="_blank" rel="noopener noreferrer">
          <button className="mercari_button">Mercari</button>
          </a>
              </div>      



      </div>
  ))
) : (
  <p>データがありません。</p>
)}
      {/* モーダル */}
      <Modal 
        isOpen={modalIsOpen} 
        onRequestClose={() => setModalIsOpen(false)} 
        contentLabel="画像サイズ選択"
        className="InventoryPicModal__Content"  // モーダルのコンテンツ部分にクラスを適用
        overlayClassName="InventoryPicModal__Overlay"  // モーダル背景にクラスを適用
      >
        <h2 className="InventoryPicModal__Header">画像の横幅を選択してください</h2>
        <div>
          <button className="InventoryPicModal__WidthButton" onClick={() => handleWidthChange(70)}>7cm</button>
          <button className="InventoryPicModal__WidthButton" onClick={() => handleWidthChange(100)}>10cm</button>
          <button className="InventoryPicModal__WidthButton" onClick={() => handleWidthChange(170)}>17cm</button>
        </div>
      </Modal>
    </div>
    </div> 
  );
});

export default InventoryTable;